import { useHistory, useLocation } from 'react-router-dom'
import { useTheme } from '@material-ui/core/styles'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Paper from '@material-ui/core/Paper'
import React, { useState, useEffect, useMemo } from 'react'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'

import { colors, constants, dates, studentCpfStorage } from '../utils'
import { appService } from '../services'
import AudienceChooser from '../components/AudienceChooser'
import RenewAction from '../components/RenewAction'
import SubscriptionActions from '../components/SubscriptionActions'
import ExistingChoice from '../components/ExistingChoice'
import PublicCallChooser from '../components/PublicCallChooser'

const description = {
  textAlign: 'center',
  margin: '20px 0 25px',
}

const buttons = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-around',
  gap: '10px',
  width: '100%',
}

const button = {
  width: 'calc(50% - 5px)',
  height: '60px',
}

const boxStyle = {
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
  width: '90%',
  maxWidth: '550px',
  padding: 30,
}

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'column',
  },
  box: boxStyle,
  boxLoading: {
    ...boxStyle,
    height: '236px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingMessage: {
    marginTop: '16px',
  },
  logo: {
    maxHeight: '88px',
    maxWidth: '550px',
    margin: '90px auto',
  },
  title: {
    textAlign: 'center',
  },
  description,
  'description-sm': {
    ...description,
    margin: '20px 0 0',
  },
  buttons,
  'buttons-sm': {
    ...buttons,
    flexDirection: 'column-reverse',
    marginTop: '25px',
  },
  button,
  'button-sm': {
    height: 'auto',
    minHeight: '56px',
    padding: '12px 16px',
    whiteSpace: 'normal',
    lineHeight: 1.25,
    textAlign: 'center',
  },
  'button-single': {
    height: '60px',
  },
}))

const Home = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const portalTheme = colors.portalTheme()

  const [loading, setLoading] = useState(true)
  const [publicCall, setPublicCall] = useState({})
  const [availableCalls, setAvailableCalls] = useState([])
  const [choosing, setChoosing] = useState(false)
  const [audience, setAudience] = useState(null)
  const [existingChoice, setExistingChoice] = useState(null)

  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.down('sm'))

  const handleChooseExisting = () => setAudience('existing')
  const handleChooseNew = () => setAudience('new')
  const handleChooseSameSchool = () => setExistingChoice('same')
  const handleChooseChangeSchool = () => setExistingChoice('change')
  const handleRenewClick = () =>
    history.push(constants.routes.renewEnrollments.request.path)
  const handleSearchClick = () =>
    history.push(constants.routes.subscriptions.search.path)
  const handleNewClick = () => history.push(constants.routes.instructions.path)
  const handleWaitingListClick = () =>
    history.push(constants.routes.waitingList.path)

  const handleBackFromExisting = () => setAudience(null)
  const handleBackFromChoice = () => setExistingChoice(null)

  const isInElectiveEnrollmentsPeriod = useMemo(() => {
    const { electiveEnrollmentsStartsAt, electiveEnrollmentsEndsAt } =
      publicCall || {}

    return dates.includesToday(
      electiveEnrollmentsStartsAt,
      electiveEnrollmentsEndsAt
    )
  }, [publicCall])

  const renderBackButton = () => {
    if (audience === null) return null

    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}
      >
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          onClick={
            audience === 'existing' && existingChoice !== null
              ? handleBackFromChoice
              : handleBackFromExisting
          }
        >
          Voltar
        </Button>
      </div>
    )
  }

  const renderWaitingList = () => {
    if (!isInElectiveEnrollmentsPeriod) return null

    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}
      >
        <Button
          className={classes[sm ? 'button-sm' : 'button']}
          variant="contained"
          color="primary"
          onClick={handleWaitingListClick}
        >
          Lista de espera
        </Button>
      </div>
    )
  }

  const renderActions = () => {
    const chooserEnabled = publicCall.renewEnrollmentPeriodMatches
    const showRenewDirect = publicCall.renew

    if (chooserEnabled) {
      if (audience === null) {
        return (
          <>
            <AudienceChooser
              publicCall={publicCall}
              classes={classes}
              sm={sm}
              onChooseExisting={handleChooseExisting}
              onChooseNew={handleChooseNew}
            />
            {renderWaitingList()}
            {renderBackButton()}
          </>
        )
      }

      if (audience === 'existing') {
        if (existingChoice === null) {
          return (
            <>
              <ExistingChoice
                classes={classes}
                publicCall={publicCall}
                sm={sm}
                onChooseSameSchool={handleChooseSameSchool}
                onChooseChange={handleChooseChangeSchool}
              />
              {renderWaitingList()}
              {renderBackButton()}
            </>
          )
        }

        if (existingChoice === 'same') {
          return (
            <>
              <RenewAction
                classes={classes}
                sm={sm}
                onClick={handleRenewClick}
              />
              {renderWaitingList()}
              {renderBackButton()}
            </>
          )
        }

        return (
          <>
            <SubscriptionActions
              classes={classes}
              sm={sm}
              onSearch={handleSearchClick}
              onNew={handleNewClick}
              showNew={publicCall.subscribe}
            />
            {renderWaitingList()}
            {renderBackButton()}
          </>
        )
      }

      return (
        <>
          <SubscriptionActions
            classes={classes}
            sm={sm}
            onSearch={handleSearchClick}
            onNew={handleNewClick}
            showNew={publicCall.subscribe}
          />
          {renderWaitingList()}
          {renderBackButton()}
        </>
      )
    }

    if (showRenewDirect) {
      return (
        <>
          <RenewAction classes={classes} sm={sm} onClick={handleRenewClick} />
          {renderWaitingList()}
          {renderBackButton()}
        </>
      )
    }

    return (
      <>
        <SubscriptionActions
          classes={classes}
          sm={sm}
          onSearch={handleSearchClick}
          onNew={handleNewClick}
          showNew={publicCall.subscribe}
          isInElectiveEnrollmentsPeriod={isInElectiveEnrollmentsPeriod}
          onWaitingListClick={handleWaitingListClick}
        />
        {renderWaitingList()}
        {renderBackButton()}
      </>
    )
  }

  const tryToFindInfo = async () => {
    const {
      success,
      payload: { info },
    } = await appService.getPublicCallInfo()

    if (success) {
      setPublicCall(info)
      setLoading(false)
      return
    }

    setLoading(!appService.getLocalPublicCall().name)
  }

  // Uma chamada só é o caso de sempre: escolhemos por ele, para não inventar um
  // clique onde não há decisão a tomar. A pergunta aparece quando a mantenedora
  // realmente tem mais de um processo aberto.
  const start = async () => {
    // Com a escolha já guardada sabemos a qual processo pertence o que está em
    // cache, então ele aparece de imediato enquanto a rede confirma. Sem escolha
    // feita, esperamos: adiantar o processo anterior seria adiantar o errado.
    if (appService.chosenPublicCallId()) {
      const local = appService.getLocalPublicCall()

      if (local.name) {
        setPublicCall(local)
        setLoading(false)
      }
    }

    const calls = await appService.getAvailablePublicCalls()

    setAvailableCalls(calls)

    if (appService.dropStaleChoice(calls)) setPublicCall({})

    if (calls.length > 1 && !appService.chosenPublicCallId()) {
      setChoosing(true)
      setLoading(false)

      return
    }

    if (calls.length === 1) {
      appService.choosePublicCall(calls[0].id)
    }

    tryToFindInfo()
  }

  // Recarrega em vez de seguir na mesma página: as rotas condicionais são
  // montadas uma vez só, com as permissões da chamada em cache. Sem recarregar,
  // quem escolhesse um processo com outras permissões veria o botão na home e
  // cairia em "página não encontrada" ao clicar.
  const handleChoosePublicCall = id => {
    appService.choosePublicCall(id)

    const from = location.state && location.state.from

    window.location.replace(from && from.startsWith('/') ? from : '/')
  }

  const handleChangePublicCall = () => {
    appService.forgetChosenPublicCall()
    setPublicCall({})
    setAudience(null)
    setExistingChoice(null)
    setChoosing(true)
  }

  useEffect(() => {
    start()
  }, [])

  useEffect(() => {
    window.initialData = undefined
    studentCpfStorage.clearStudentCpf()
  }, [])

  const renderEmpty = () => (
    <Paper className={classes.boxLoading}>
      <Typography className={classes.loadingMessage} variant="subtitle1">
        Nenhum processo aberto no momento
      </Typography>
    </Paper>
  )

  const renderLoading = () => (
    <Paper className={classes.boxLoading}>
      <CircularProgress size={40} />
      <Typography className={classes.loadingMessage} variant="subtitle1">
        Carregando...
      </Typography>
    </Paper>
  )

  const renderChooser = () => (
    <Paper className={classes.box}>
      <Typography className={classes.title} variant="h5">
        Chamada Escolar
      </Typography>

      <PublicCallChooser
        publicCalls={availableCalls}
        classes={classes}
        sm={sm}
        onChoose={handleChoosePublicCall}
      />
    </Paper>
  )

  // Só faz sentido oferecer a troca quando há para onde trocar. Sem isto, quem
  // escolhesse errado ficaria preso: a escolha fica guardada no navegador.
  const renderChangePublicCall = () => {
    if (availableCalls.length < 2) return null

    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}
      >
        <Button color="primary" size="small" onClick={handleChangePublicCall}>
          Escolher outro processo
        </Button>
      </div>
    )
  }

  const renderContent = () => (
    <Paper className={classes.box}>
      <Typography className={classes.title} variant="h5">
        {availableCalls.length > 1
          ? publicCall.name
          : `Chamada Escolar ${publicCall.schoolYearName}`}
      </Typography>

      <Typography
        className={classes[sm ? 'description-sm' : 'description']}
        variant="body1"
      >
        Seja bem-vindo(a) ao portal da Chamada Escolar
      </Typography>

      {renderActions()}
      {renderChangePublicCall()}
    </Paper>
  )

  const renderAppropriate = () => {
    if (loading) {
      return renderLoading()
    } else if (choosing) {
      return renderChooser()
    } else if (publicCall.name) {
      return renderContent()
    }

    return renderEmpty()
  }

  return (
    <>
      <div className={classes.root}>
        {portalTheme.logo && (
          <img
            className={classes.logo}
            src={portalTheme.logo}
            alt={`Logo - ${portalTheme.title}`}
          />
        )}

        {renderAppropriate()}
      </div>
    </>
  )
}

export default Home
